import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { CardSaveBadge } from './CardSaveBadge';
import { useCardSaveBadges } from '../../hooks/useCardSaveBadges';
import { useTenantApiToken } from '../../contexts/TenantProfileContext';
import {
  fetchProspectGroup,
  putProspectGroupProductPreferences,
  type GroupProductPreferenceSide,
} from '../../services/tenantApi';

const BEDROOM_OPTIONS = ['1', '2', '3', '4', '5', '6', '7+'] as const;
const BATHROOM_OPTIONS = ['1', '2', '3', '4'] as const;
const CATEGORIES = [
  { key: 'bathrooms', label: 'Bathrooms' },
  { key: 'laundry', label: 'Laundry' },
  { key: 'kitchen', label: 'Kitchen' },
  { key: 'commonSpace', label: 'Common space' },
  { key: 'parking', label: 'Parking' },
  { key: 'furnished', label: 'Furnished' },
  { key: 'enSuiteBathroom', label: 'En suite bathroom' },
  { key: 'outdoorSpace', label: 'Outdoor space' },
  { key: 'rentPerBedroom', label: 'Rent per bedroom' },
] as const;

type CategoryKey = (typeof CATEGORIES)[number]['key'];

const CHOICE_RANKS = ['1st', '2nd', '3rd'] as const;

const RENT_PER_BEDROOM_MIN = 750;
const RENT_PER_BEDROOM_MAX = 2800;
const RENT_PER_BEDROOM_STEP = 50;

function rentPerBedroomPrices(): number[] {
  const prices: number[] = [];
  for (let price = RENT_PER_BEDROOM_MIN; price <= RENT_PER_BEDROOM_MAX; price += RENT_PER_BEDROOM_STEP) {
    prices.push(price);
  }
  return prices;
}

const RENT_PER_BEDROOM_PRICES = rentPerBedroomPrices();

function formatRentPrice(price: number): string {
  return `$${price.toLocaleString('en-US')}`;
}

function nearestRentPriceIndex(price: number): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  RENT_PER_BEDROOM_PRICES.forEach((candidate, index) => {
    const distance = Math.abs(candidate - price);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });
  return best;
}

/** Visual bar height only. It is not a count of homes at that price. */
function rentBarHeight(index: number): string {
  const t = index / (RENT_PER_BEDROOM_PRICES.length - 1);
  const wave = Math.sin(Math.PI * t) ** 0.8;
  return `${Math.round(16 + wave * 84)}%`;
}

function withoutDuplicateChoices(side: GroupProductPreferenceSide): GroupProductPreferenceSide {
  const bedrooms = [...new Set(side.values.bedrooms)];
  const bathrooms = [...new Set(side.values.bathrooms)];
  if (
    bedrooms.length === side.values.bedrooms.length
    && bathrooms.length === side.values.bathrooms.length
  ) {
    return side;
  }
  return {
    ...side,
    values: { ...side.values, bedrooms, bathrooms },
  };
}

function emptySide(): GroupProductPreferenceSide {
  return {
    values: {
      bedrooms: [],
      bathrooms: [],
      laundry: null,
      parking: null,
      furnished: null,
      enSuiteBathroom: null,
      outdoorSpace: null,
      rentPerBedroom: { min: null, max: null },
      location: { zones: [] },
    },
    priority: [],
  };
}

function categoryLabel(key: string): string {
  return CATEGORIES.find((category) => category.key === key)?.label ?? key;
}

function preferencesAreComplete(side: GroupProductPreferenceSide): boolean {
  const { values, priority } = side;
  const rent = values.rentPerBedroom;
  const everyChoiceSet =
    values.bedrooms.length > 0 &&
    values.bathrooms.length > 0 &&
    values.laundry != null &&
    values.parking != null &&
    values.furnished != null &&
    values.enSuiteBathroom != null &&
    values.outdoorSpace != null &&
    (rent.min != null || rent.max != null);
  const rankingMoved = CATEGORIES.some((category) => priority.includes(category.key));
  return everyChoiceSet && rankingMoved;
}

interface GroupProductPreferencesCardProps {
  className?: string;
  onCompleteChange?: (complete: boolean) => void;
  /** Bedroom counts the lead contact just turned on. Never removes a preference. */
  leadBedrooms?: string[];
}

export default function GroupProductPreferencesCard({
  className = '',
  onCompleteChange,
  leadBedrooms,
}: GroupProductPreferencesCardProps) {
  const getToken = useTenantApiToken();
  const [side, setSide] = useState<GroupProductPreferenceSide>(emptySide);
  const [ready, setReady] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const lastSavedRef = useRef('');
  const sideRef = useRef(side);
  const saveGeneration = useRef(0);
  sideRef.current = side;

  useEffect(() => {
    let cancelled = false;
    void fetchProspectGroup(getToken)
      .then((group) => {
        if (cancelled) return;
        const stated = group.productPreferences?.stated ?? emptySide();
        setSide(stated);
        lastSavedRef.current = JSON.stringify(stated);
        setReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load group product preferences.');
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!ready) return;
    const snapshot = JSON.stringify(side);
    if (snapshot === lastSavedRef.current) return;
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      const savingSide = withoutDuplicateChoices(sideRef.current);
      const savingSnapshot = JSON.stringify(savingSide);
      if (savingSnapshot === lastSavedRef.current) return;
      setSaveState('saving');
      void putProspectGroupProductPreferences(getToken, savingSide)
        .then((group) => {
          if (cancelled || JSON.stringify(withoutDuplicateChoices(sideRef.current)) !== savingSnapshot) return;
          const stated = group.productPreferences?.stated ?? savingSide;
          const echoed = JSON.stringify(stated);
          lastSavedRef.current = echoed;
          if (JSON.stringify(sideRef.current) !== echoed) setSide(stated);
          saveGeneration.current = 0;
          setSaveState('saved');
          setError(null);
        })
        .catch((err: unknown) => {
          if (cancelled || JSON.stringify(withoutDuplicateChoices(sideRef.current)) !== savingSnapshot) return;
          const message = err instanceof Error ? err.message : 'Could not save group product preferences.';
          if (message === 'The save did not finish. Try again.' && saveGeneration.current < 2) {
            saveGeneration.current += 1;
            window.setTimeout(() => {
              if (JSON.stringify(withoutDuplicateChoices(sideRef.current)) === savingSnapshot) {
                setSide((current) => ({ ...current }));
              }
            }, 800);
            return;
          }
          saveGeneration.current = 0;
          setSaveState('error');
          setError(message);
        });
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [getToken, ready, side]);

  const update = (next: GroupProductPreferenceSide) => setSide(next);

  const toggleBedroom = (option: string) => {
    const current = side.values.bedrooms;
    if (current.includes(option)) {
      update({ ...side, values: { ...side.values, bedrooms: current.filter((value) => value !== option) } });
      return;
    }
    if (current.length >= 3) return;
    update({ ...side, values: { ...side.values, bedrooms: [...current, option] } });
  };

  const toggleBathroom = (option: string) => {
    const current = side.values.bathrooms;
    update({
      ...side,
      values: {
        ...side.values,
        bathrooms: current.includes(option) ? current.filter((value) => value !== option) : [...current, option],
      },
    });
  };

  const setRent = (min: number | null, max: number | null) => {
    update({
      ...side,
      values: { ...side.values, rentPerBedroom: { min, max } },
    });
  };

  const reorderPriority = (priority: CategoryKey[]) => {
    const next: string[] = [...priority];
    const hidden = ['bedrooms', 'location']
      .map((key) => ({ key, index: side.priority.indexOf(key) }))
      .filter((item) => item.index !== -1)
      .sort((a, b) => a.index - b.index);
    for (const item of hidden) {
      next.splice(Math.min(item.index, next.length), 0, item.key);
    }
    update({ ...side, priority: next });
  };

  const complete = preferencesAreComplete(side);

  useEffect(() => {
    if (!ready || !leadBedrooms || leadBedrooms.length === 0) return;
    setSide((current) => {
      const bedrooms = [...new Set(current.values.bedrooms)];
      const incoming = [...new Set(leadBedrooms)];
      const missing = incoming.filter((bedroom) => !bedrooms.includes(bedroom));
      const unchanged = missing.length === 0 && bedrooms.length === current.values.bedrooms.length;
      if (unchanged) return current;
      return {
        ...current,
        values: {
          ...current.values,
          bedrooms: [...bedrooms, ...missing].slice(0, 3),
        },
      };
    });
  }, [leadBedrooms, ready]);

  useEffect(() => {
    if (!ready) return;
    onCompleteChange?.(complete);
  }, [complete, onCompleteChange, ready]);

  const preferenceSave = useCardSaveBadges(
    { preferences: JSON.stringify(side) },
    saveState === 'error' ? 'error' : saveState,
    ready,
    null,
  );

  return (
    <section className={`relative rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-bento dark:border-white/10 dark:bg-gray-800 sm:px-6 sm:py-5 ${className}`.trim()}>
      <CardSaveBadge phase={preferenceSave.preferences} complete={complete} />
      <div className="flex items-start justify-between gap-3 pr-10">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Group Product Preferences</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Tell us about your group's product preferences.
          </p>
        </div>
      </div>

      {error ? <p className="mt-3 text-sm font-medium text-red-700 dark:text-red-400">{error}</p> : null}

      <div className="mt-6 grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-10">
        <div className="min-w-0">
          <StepHeading step={1} title="What matters most to you" />
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">Drag to rank these. The first one matters most.</p>
          <PriorityCards priority={side.priority} disabled={!ready} onReorder={reorderPriority} />
        </div>
        <div className="min-w-0">
          <StepHeading step={2} title="Setting your budget" />
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            Drag across the chart to set a lower and upper rent. Drag either side to adjust.
          </p>
          <RentPerBedroomChart
            min={side.values.rentPerBedroom.min}
            max={side.values.rentPerBedroom.max}
            disabled={!ready}
            onChange={setRent}
          />

          <div className="mt-8">
            <StepHeading step={3} title="Other details" />
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
          <DetailRow title="Bedrooms">
            {BEDROOM_OPTIONS.map((option) => {
              const rank = side.values.bedrooms.indexOf(option);
              const selected = rank >= 0;
              return (
                <ChoiceChip
                  key={option}
                  label={`${option} bed${option === '1' ? '' : 's'}`}
                  rank={selected ? CHOICE_RANKS[rank] : undefined}
                  selected={selected}
                  disabled={!ready || (!selected && side.values.bedrooms.length >= 3)}
                  onClick={() => toggleBedroom(option)}
                />
              );
            })}
          </DetailRow>

          <DetailRow title="Bathrooms">
            {BATHROOM_OPTIONS.map((option) => (
              <ChoiceChip
                key={option}
                label={`${option} bath${option === '1' ? '' : 's'}`}
                selected={side.values.bathrooms.includes(option)}
                disabled={!ready}
                onClick={() => toggleBathroom(option)}
              />
            ))}
          </DetailRow>

          <DetailRow title="Laundry">
            {(
              [
                { value: 'in_unit', label: 'In the home' },
                { value: 'in_building', label: 'In the building' },
                { value: 'either', label: 'Either' },
              ] as const
            ).map((option) => (
              <ChoiceChip
                key={option.value}
                label={option.label}
                selected={side.values.laundry === option.value}
                disabled={!ready}
                onClick={() => update({ ...side, values: { ...side.values, laundry: option.value } })}
              />
            ))}
          </DetailRow>

          <DetailRow title="Outdoor space">
            {(
              [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
                { value: 'not_applicable', label: 'Not applicable' },
              ] as const
            ).map((option) => (
              <ChoiceChip
                key={option.value}
                label={option.label}
                selected={side.values.outdoorSpace === option.value}
                disabled={!ready}
                onClick={() => update({ ...side, values: { ...side.values, outdoorSpace: option.value } })}
              />
            ))}
          </DetailRow>

          <DetailRow title="Parking">
            {(
              [
                { value: 'required', label: 'Required' },
                { value: 'preferred', label: 'Nice to have' },
                { value: 'not_needed', label: 'Not needed' },
              ] as const
            ).map((option) => (
              <ChoiceChip
                key={option.value}
                label={option.label}
                selected={side.values.parking === option.value}
                disabled={!ready}
                onClick={() => update({ ...side, values: { ...side.values, parking: option.value } })}
              />
            ))}
          </DetailRow>

          <DetailRow title="Furnished">
            {(
              [
                { value: 'furnished', label: 'Furnished' },
                { value: 'unfurnished', label: 'Unfurnished' },
                { value: 'either', label: 'Either' },
              ] as const
            ).map((option) => (
              <ChoiceChip
                key={option.value}
                label={option.label}
                selected={side.values.furnished === option.value}
                disabled={!ready}
                onClick={() => update({ ...side, values: { ...side.values, furnished: option.value } })}
              />
            ))}
          </DetailRow>

          <DetailRow title="En suite bathroom">
            {(
              [
                { value: 'required', label: 'Required' },
                { value: 'preferred', label: 'Nice to have' },
                { value: 'not_needed', label: 'Not needed' },
              ] as const
            ).map((option) => (
              <ChoiceChip
                key={option.value}
                label={option.label}
                selected={side.values.enSuiteBathroom === option.value}
                disabled={!ready}
                onClick={() => update({ ...side, values: { ...side.values, enSuiteBathroom: option.value } })}
              />
            ))}
          </DetailRow>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const RENT_AXIS_TICKS = [750, 1550, 2150, 2800] as const;

function RentPerBedroomChart({
  min,
  max,
  disabled,
  onChange,
}: {
  min: number | null;
  max: number | null;
  disabled: boolean;
  onChange: (min: number | null, max: number | null) => void;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    mode: 'draw' | 'min' | 'max' | 'window';
    startIndex: number;
    minIndex: number;
    maxIndex: number;
    moved: boolean;
  } | null>(null);

  const low = min == null ? max : max == null ? min : Math.min(min, max);
  const high = min == null || max == null ? low : Math.max(min, max);
  const lowIndex = low == null ? null : nearestRentPriceIndex(low);
  const highIndex = high == null ? null : nearestRentPriceIndex(high);
  const lastIndex = RENT_PER_BEDROOM_PRICES.length - 1;
  const summary =
    low == null
      ? 'No rent range yet.'
      : high == null || high === low
        ? `${formatRentPrice(low)} per bedroom`
        : `${formatRentPrice(low)} to ${formatRentPrice(high)} per bedroom`;
  const labelsOverlap = lowIndex != null && highIndex != null && highIndex - lowIndex < 8;

  const commitIndexes = (start: number, end: number) => {
    const nextMin = RENT_PER_BEDROOM_PRICES[Math.min(start, end)];
    const nextMax = RENT_PER_BEDROOM_PRICES[Math.max(start, end)];
    onChange(nextMin, nextMax);
  };

  const indexFromClientX = (clientX: number) => {
    const rect = chartRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    const ratio = (clientX - rect.left) / rect.width;
    return Math.min(lastIndex, Math.max(0, Math.round(ratio * RENT_PER_BEDROOM_PRICES.length - 0.5)));
  };

  const barCenterPercent = (index: number) => ((index + 0.5) / RENT_PER_BEDROOM_PRICES.length) * 100;
  const rangeStartPercent = (index: number) => (index / RENT_PER_BEDROOM_PRICES.length) * 100;
  const rangeEndPercent = (index: number) => ((index + 1) / RENT_PER_BEDROOM_PRICES.length) * 100;

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || event.button !== 0) return;
    const index = indexFromClientX(event.clientX);
    const handle = (event.target as HTMLElement).closest('[data-rent-handle]')?.getAttribute('data-rent-handle');
    const inside =
      lowIndex != null && highIndex != null && index >= lowIndex && index <= highIndex && lowIndex !== highIndex;
    dragRef.current = {
      pointerId: event.pointerId,
      mode: handle === 'min' ? 'min' : handle === 'max' ? 'max' : inside ? 'window' : 'draw',
      startIndex: index,
      minIndex: lowIndex ?? index,
      maxIndex: highIndex ?? index,
      moved: false,
    };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Untrusted or already-ended pointers cannot be captured. The chart still tracks the drag.
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const index = indexFromClientX(event.clientX);
    if (index !== drag.startIndex) drag.moved = true;
    if (!drag.moved) return;
    if (drag.mode === 'window') {
      const span = drag.maxIndex - drag.minIndex;
      let nextMin = drag.minIndex + (index - drag.startIndex);
      let nextMax = nextMin + span;
      if (nextMin < 0) {
        nextMin = 0;
        nextMax = span;
      }
      if (nextMax > lastIndex) {
        nextMax = lastIndex;
        nextMin = lastIndex - span;
      }
      commitIndexes(nextMin, nextMax);
      return;
    }
    if (drag.mode === 'min') {
      commitIndexes(Math.min(index, drag.maxIndex), drag.maxIndex);
      return;
    }
    if (drag.mode === 'max') {
      commitIndexes(drag.minIndex, Math.max(index, drag.minIndex));
      return;
    }
    commitIndexes(drag.startIndex, index);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.moved && drag.mode === 'draw') {
      if (lowIndex == null || highIndex == null) {
        commitIndexes(drag.startIndex, drag.startIndex);
      } else if (drag.startIndex < lowIndex || drag.startIndex > highIndex) {
        const closerToMin = Math.abs(drag.startIndex - lowIndex) <= Math.abs(drag.startIndex - highIndex);
        commitIndexes(closerToMin ? drag.startIndex : lowIndex, closerToMin ? highIndex : drag.startIndex);
      }
    }
    dragRef.current = null;
  };

  const nudge = (which: 'min' | 'max', step: number) => {
    if (lowIndex == null || highIndex == null) {
      commitIndexes(0, Math.min(lastIndex, 1));
      return;
    }
    if (which === 'min') {
      commitIndexes(Math.min(Math.max(0, lowIndex + step), highIndex), highIndex);
      return;
    }
    commitIndexes(lowIndex, Math.max(Math.min(lastIndex, highIndex + step), lowIndex));
  };

  return (
    <div data-rent-chart-block="">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{summary}</p>
        {low != null ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(null, null)}
            className="text-xs font-medium text-gray-500 dark:text-gray-400 disabled:opacity-40"
          >
            Clear
          </button>
        ) : null}
      </div>
      <div
        ref={chartRef}
        data-rent-chart=""
        className="relative h-32 touch-none select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="absolute inset-x-0 top-7 bottom-4 border-b border-gray-200 dark:border-white/10" aria-hidden="true">
          <div className="relative flex h-full items-end">
          {lowIndex != null && highIndex != null ? (
            <div
              className="absolute inset-y-0 rounded-sm bg-blue-50 dark:bg-blue-950/60"
              data-rent-window=""
              style={{
                left: `${(lowIndex / RENT_PER_BEDROOM_PRICES.length) * 100}%`,
                width: `${((highIndex - lowIndex + 1) / RENT_PER_BEDROOM_PRICES.length) * 100}%`,
              }}
            />
          ) : null}
          {RENT_PER_BEDROOM_PRICES.map((price, index) => {
            const selected = lowIndex != null && highIndex != null && index >= lowIndex && index <= highIndex;
            return (
              <div key={price} className="flex h-full flex-1 items-end px-0.5">
                <div
                  className={`w-full rounded-t-[3px] ${selected ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                  style={{ height: rentBarHeight(index) }}
                />
              </div>
            );
          })}
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-7">
          {labelsOverlap && lowIndex != null && highIndex != null ? (
            <span
              className="absolute top-0 whitespace-nowrap text-xs font-semibold text-blue-700 dark:text-blue-300"
              style={rentLabelStyle((rangeStartPercent(lowIndex) + rangeEndPercent(highIndex)) / 2)}
            >
              {lowIndex === highIndex
                ? formatRentPrice(RENT_PER_BEDROOM_PRICES[lowIndex])
                : `${formatRentPrice(RENT_PER_BEDROOM_PRICES[lowIndex])}–${formatRentPrice(RENT_PER_BEDROOM_PRICES[highIndex])}`}
            </span>
          ) : null}
          {lowIndex != null && !labelsOverlap ? (
            <span className="absolute top-0 text-xs font-semibold text-blue-700 dark:text-blue-300" style={rentLabelStyle(rangeStartPercent(lowIndex))}>
              {formatRentPrice(RENT_PER_BEDROOM_PRICES[lowIndex])}
            </span>
          ) : null}
          {highIndex != null && lowIndex !== highIndex && !labelsOverlap ? (
            <span className="absolute top-0 text-xs font-semibold text-blue-700 dark:text-blue-300" style={rentLabelStyle(rangeEndPercent(highIndex))}>
              {formatRentPrice(RENT_PER_BEDROOM_PRICES[highIndex])}
            </span>
          ) : null}
        </div>
        {lowIndex == null ? (
          <>
            <span className="pointer-events-none absolute bottom-4 top-7 left-[12%] border-l-2 border-dashed border-gray-400" />
            <span className="pointer-events-none absolute bottom-1 left-[12%] -translate-x-1/2 text-lg font-bold leading-none text-gray-400">&lt;</span>
            <span className="pointer-events-none absolute bottom-4 top-7 left-[88%] border-l-2 border-dashed border-gray-400" />
            <span className="pointer-events-none absolute bottom-1 left-[88%] -translate-x-1/2 text-lg font-bold leading-none text-gray-400">&gt;</span>
          </>
        ) : null}
        {lowIndex != null ? (
          <RentHandle
            which="min"
            percent={lowIndex === highIndex ? rangeEndPercent(lowIndex) : rangeStartPercent(lowIndex)}
            price={RENT_PER_BEDROOM_PRICES[lowIndex]}
            maxPrice={high == null ? RENT_PER_BEDROOM_PRICES[lowIndex] : high}
            disabled={disabled}
            onNudge={(step) => nudge('min', step)}
          />
        ) : null}
        {highIndex != null && highIndex !== lowIndex ? (
          <RentHandle
            which="max"
            percent={rangeEndPercent(highIndex)}
            price={RENT_PER_BEDROOM_PRICES[highIndex]}
            minPrice={low ?? RENT_PER_BEDROOM_PRICES[highIndex]}
            disabled={disabled}
            onNudge={(step) => nudge('max', step)}
          />
        ) : null}
      </div>
      <div className="relative mt-1 h-4 text-[11px] text-gray-400 dark:text-gray-500" aria-hidden="true">
        {RENT_AXIS_TICKS.map((tick) => (
          <span key={tick} className="absolute top-0" style={rentLabelStyle(barCenterPercent(nearestRentPriceIndex(tick)))}>
            {formatRentPrice(tick)}
          </span>
        ))}
      </div>
    </div>
  );
}

function rentLabelStyle(percent: number): CSSProperties {
  if (percent < 8) return { left: 0 };
  if (percent > 92) return { right: 0 };
  return { left: `${percent}%`, transform: 'translateX(-50%)' };
}

function RentHandle({
  which,
  percent,
  price,
  minPrice,
  maxPrice,
  disabled,
  onNudge,
}: {
  which: 'min' | 'max';
  percent: number;
  price: number;
  minPrice?: number;
  maxPrice?: number;
  disabled: boolean;
  onNudge: (step: number) => void;
}) {
  return (
    <button
      type="button"
      data-rent-handle={which}
      role="slider"
      aria-label={which === 'min' ? 'Lower rent per bedroom' : 'Upper rent per bedroom'}
      aria-valuemin={minPrice ?? RENT_PER_BEDROOM_MIN}
      aria-valuemax={maxPrice ?? RENT_PER_BEDROOM_MAX}
      aria-valuenow={price}
      aria-valuetext={`${formatRentPrice(price)} per bedroom`}
      disabled={disabled}
      style={{ left: `${percent}%` }}
      className="absolute bottom-4 top-7 z-10 w-8 -translate-x-1/2 cursor-ew-resize focus-visible:outline-none disabled:opacity-40"
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
          event.preventDefault();
          onNudge(-1);
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
          event.preventDefault();
          onNudge(1);
        }
      }}
    >
      <span className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 border-l-2 border-solid border-blue-600" />
      <span className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 text-lg font-bold leading-none text-blue-700">
        {which === 'min' ? '<' : '>'}
      </span>
    </button>
  );
}

function orderedCategoryKeys(priority: readonly string[]): CategoryKey[] {
  const known = new Set<string>(CATEGORIES.map((category) => category.key));
  const ranked = priority.filter((key): key is CategoryKey => known.has(key));
  const rest = CATEGORIES.map((category) => category.key).filter((key) => !ranked.includes(key));
  return [...ranked, ...rest];
}

function PriorityCards({
  priority,
  disabled,
  onReorder,
}: {
  priority: string[];
  disabled: boolean;
  onReorder: (priority: CategoryKey[]) => void;
}) {
  const order = useMemo(() => orderedCategoryKeys(priority), [priority]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id as CategoryKey);
    const newIndex = order.indexOf(over.id as CategoryKey);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(order, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <ol className="space-y-2">
          {order.map((key, index) => (
            <PriorityCard key={key} id={key} rank={index + 1} label={categoryLabel(key)} disabled={disabled} />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function PriorityCard({
  id,
  rank,
  label,
  disabled,
}: {
  id: CategoryKey;
  rank: number;
  label: string;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-xl border bg-white px-3 py-2.5 dark:bg-gray-950 dark:text-gray-100 ${
        isDragging ? 'relative z-10 border-blue-300 shadow-md dark:border-blue-400/50' : 'border-gray-200 dark:border-white/10'
      }`}
    >
      <button
        type="button"
        className="inline-flex h-9 w-8 shrink-0 cursor-grab items-center justify-center rounded-md text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-600 dark:hover:text-gray-300 active:cursor-grabbing disabled:cursor-default disabled:opacity-40"
        aria-label={`Drag to reorder ${label}`}
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-700 text-xs font-semibold text-white">
        {rank}
      </span>
      <span className="min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-white">{label}</span>
    </li>
  );
}

function StepHeading({ step, title }: { step: number; title: string }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white dark:bg-white dark:text-gray-900">
        {step}
      </div>
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{title}</span>
      <div className="min-w-[2rem] flex-1 border-t border-dashed border-gray-300 dark:border-white/20" />
    </div>
  );
}

function DetailRow({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-2 border-t border-gray-200 px-4 py-3 first:border-t-0 sm:grid-cols-[11rem_minmax(0,1fr)] sm:items-center sm:gap-6 dark:border-white/10">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-900 dark:text-white">{title}</div>
        {hint ? <p className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">{hint}</p> : null}
      </div>
      <div className="flex min-w-0 flex-wrap gap-2">{children}</div>
    </div>
  );
}

function ChoiceChip({
  label,
  rank,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  rank?: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
        selected
          ? 'border-red-700 bg-red-700 text-white'
          : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50 dark:border-white/15 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-900'
      }`}
    >
      <span>{label}</span>
      {rank ? <span className={`text-[10px] font-semibold uppercase tracking-wide ${selected ? 'text-white/80' : 'text-gray-400'}`}>{rank}</span> : null}
    </button>
  );
}
